import { isNumber } from 'lodash';
import merge from 'lodash/merge';
import moment, { isMoment } from 'moment-timezone';

export function parseDotKey(key, value) {
  const result = {};
  if (key.indexOf('.') >= 0) {
    const [baseKey, nestedKey] = key.split('.');
    result[baseKey] = {
      [nestedKey]: value,
    };
  } else {
    result[key] = value;
  }
  return result;
}

export function parseTopLevelDotKeys(object) {
  return Object.entries(object).reduce(
    (prev, [key, value]) => merge({}, prev, parseDotKey(key, value)),
    {}
  );
}

const isObjectTypeofDate = (object) => !!object && typeof object === 'object';

export const parseDateToTimezone = (date, tz) => moment(date).tz(tz);

export const formatToDate = (date) => moment(date).format('YYYY-MM-DD');

export const parseDateToString = (date) => {
  if (!date) {
    return date;
  }

  const parsedDate = isMoment(date) ? date : moment(date);

  return parsedDate.format('YYYY-MM-DD');
};

export const formatDate = (date) => {
  if (!date) {
    return date;
  }

  const dateObject = moment(date);

  return dateObject.format('YYYY-MM-DD');
};

export const parseDateToTimeString = (date) => {
  // convert date object to HH:mm
  const dateObject = isObjectTypeofDate(date) ? date : new Date(date);

  return `${`0${dateObject.getHours()}`.slice(-2)}:${`0${dateObject.getMinutes()}`.slice(-2)}`;
};

export const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000;

export const isTimestampsFromSameDay = (timestamp1, timestamp2) => {
  const format = 'YYYY-MM-DD';
  const date1 = new Date(moment(timestamp1).format(format));
  const date2 = new Date(moment(timestamp2).format(format));

  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

export const hasAvailability = (item, date = new Date()) => {
  if (Array.isArray(item.availability)) {
    return item.availability.length > 0
      ? item.availability &&
          !!item.availability.find((option) => isTimestampsFromSameDay(option.startDateTime, date))
      : false;
  }
  return !!item.availability;
};

export const isDateFromPast = (date) => new Date(date).getTime() < new Date().getTime();

const compareByPrice = (a, b) => {
  if (a.price.amount > b.price.amount) return 1;
  if (a.price.amount < b.price.amount) return -1;
  return 0;
};

export const compareByAvailability = (a, b, date) => {
  const aHasAvailability = hasAvailability(a, date);
  const bHasAvailability = hasAvailability(b, date);
  if (aHasAvailability && bHasAvailability) {
    return compareByPrice(a, b);
  }
  if (aHasAvailability) {
    return -1;
  }
  if (bHasAvailability) {
    return 1;
  }
  return compareByPrice(a, b);
};

export const findGuestIndexInArray = (guestArray, guest) =>
  guestArray.findIndex((element) => {
    const sameCoreId = !!guest?.externalSpaId && element?.externalSpaId === guest.externalSpaId;
    const sameName = element?.firstName === guest.firstName && element?.lastName === guest.lastName;

    return sameCoreId || sameName;
  });

export const isGuestAlreadyInArray = (guestArray = [], guest) =>
  findGuestIndexInArray(guestArray, guest) >= 0;

// Fetch first image from gallery if exists
const getImageGalleryFirstImage = (gallery) => (gallery && gallery.length ? gallery[0] : null);

// Fetch first image from gallery or static image property from item
export const getImageOrGalleryImage = (item) => {
  if (item.image) {
    return item.image;
  }
  if (item.gallery) {
    return getImageGalleryFirstImage(item.gallery);
  }
  return null;
};

const typesMap = {
  treatment: 'spa-treatment',
};

const mapType = (type) => typesMap[type] || type;

/**
 * Normalise all shop releated entities to preserve a uniform structure for sub-components.
 */
export const parseToUnified = (
  type,
  { id, name, imageGallery, image, price, categories, options, discount }
) => ({
  id,
  name,
  image:
    ['product', 'event', 'voucher'].includes(type) && imageGallery
      ? getImageGalleryFirstImage(imageGallery)
      : image,
  type,
  url: mapType(type),
  price,
  categories,
  options,
  discount,
});

/**
 * Extracts main category id basing on the selected target type
 */
export const getMainCategoryId = (item) =>
  item.crossSellTargetType === 'category' ? item.crossSellCategory?.id : null;

/**
 * Flatten singular upsell item into cross-sell array and return array
 */
export const mergeCrossSellWithUpsell = (upSellProduct, upsellType, crossSellItems = []) => {
  const items = Array.isArray(crossSellItems) ? crossSellItems : [];
  const updatedCrossSellItems = items.map((item) => ({
    ...item,
    type: item.type === 'spa-package' ? 'spa-day' : item.type,
  }));

  const slides = [...updatedCrossSellItems];

  if (upSellProduct) {
    slides.push({
      object: upSellProduct,
      type: upsellType,
    });
  }

  return slides;
};

export const isTouchSupported = () => {
  if (typeof window !== 'object') return false;
  return 'ontouchstart' in window || navigator.msMaxTouchPoints;
};

export const isViewportTooSmall = (w, h) =>
  typeof window === 'object' && (window.innerWidth < w || window.innerHeight < h);

export const getGuestGenderByTitle = (title) => {
  if (!title) {
    return null;
  }

  return title === 'Mr' ? 'male' : 'female';
};

export const getDeliverableItems = (basketDetails) =>
  basketDetails.items.filter((basketItem) => basketItem.type === 'products');

export const groupByTimeslotStartDateTime = (timeslots, spaClasses) =>
  timeslots.reduce(
    (dates, timeslot) => ({
      ...dates,
      [timeslot.startDateTime]: [
        ...(Array.isArray(dates[timeslot.startDateTime]) ? dates[timeslot.startDateTime] : []),
        {
          ...timeslot,
          spaClass: spaClasses.find(({ id }) => id === timeslot.classId),
        },
      ],
    }),
    {}
  );

export const sortEntityByPriority = (entity, priorityfield = 'priority') =>
  entity.sort((a, b) => a[priorityfield] - b[priorityfield]);

export const sortEntityByDisplayOrder = (entity) =>
  entity.sort((a, b) => a.displayOrder - b.displayOrder);

export const compareDates = (dateA, dateB) => dateA.isSame(dateB, 'day');

export const getDatesRange = (start, daysQuantity = 7) => {
  const days = new Array(daysQuantity)
    .fill()
    .map((_, index) => ({ date: moment(start).add(index, 'days') }));
  return days;
};

export const parseNumericField = (field, fieldValue, numericFields) => {
  if (numericFields.includes(field)) {
    /* eslint-disable-next-line no-restricted-globals */
    if (typeof fieldValue === 'object') {
      return Object.entries(fieldValue).reduce(
        (parsedFields, [key, value]) => ({
          ...parsedFields,
          [key]: Number.isNaN(value) ? 0 : value,
        }),
        {}
      );
    }

    return Number.isNaN(fieldValue) ? 0 : fieldValue;
  }
  return fieldValue;
};

export const parseISOString = (s) => {
  const dt = s.split(/[: T-]/).map(parseFloat);
  return new Date(dt[0], dt[1] - 1, dt[2], dt[3] || 0, dt[4] || 0, dt[5] || 0, 0);
};

export const parseTime = (time) => {
  const timeArray = time.match(/(0[0-9]|1[0-9]|2[0-3]|[0-9]):[0-5][0-9]/);
  return timeArray.length ? timeArray[0] : null;
};

export const getLeavetime = (timeslot, promotionId) => {
  if (!timeslot) {
    return null;
  }

  const foundPromotion = timeslot.availablePromotions.find(
    (promotion) => promotion.id === promotionId
  );

  const { leaveTime } = foundPromotion || timeslot;
  return leaveTime;
};

export const parseTimeSlots = (timeSlots = []) => {
  return timeSlots.length === 0
    ? []
    : timeSlots
        .map(({ date, data: { timeSlots } }) =>
          timeSlots.map(({ timeSlot: slot }) => ({
            value: slot,
            label: parseDateToTimeString(parseISOString(slot)),
            date,
          }))
        )
        .reduce((prev, cur) => [...prev, ...cur]);
};

export const parseTimeslotsNew = (timeSlots = [], date) => {
  return timeSlots.length === 0
    ? []
    : timeSlots.map(({ timeSlot: slot }) => ({
        value: slot,
        label: parseDateToTimeString(parseISOString(slot)),
        date,
      }));
};

export const simplifyLoggedInGuest = (loggedInGuest) =>
  loggedInGuest && loggedInGuest.firstName && loggedInGuest.lastName
    ? {
        ...loggedInGuest,
        firstName: loggedInGuest.firstName,
        lastName: loggedInGuest.lastName,
        title: loggedInGuest.title,
        gender: loggedInGuest.gender || getGuestGenderByTitle(loggedInGuest.title),
        loggedInUser: { id: loggedInGuest.id },
        ...(loggedInGuest.externalSpaId
          ? { externalSpaId: loggedInGuest.externalSpaId }
          : { isLead: true }),
      }
    : null;

export const getCheapestOption = (options) =>
  options.reduce((cheapestOption, currentOption) => {
    const _getPrice = (option) => {
      if (!option?.price) return null;

      if (isNumber(option.price)) return option.price;

      if (typeof option.price != 'object') return null;

      return option?.price?.['amount'];
    };

    const cheapestPrice = _getPrice(cheapestOption);

    const currentPrice = _getPrice(currentOption);

    if (cheapestPrice == null || cheapestPrice == undefined) return currentOption;

    return cheapestPrice < currentPrice ? cheapestOption : currentOption;
  });
